import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type {
  AppApi,
  BindingConfig,
  FieldBindingConfig,
} from '@wix/wix-data-client-common'
import { cloneDeep, isEmpty } from 'lodash'
import { BINDING_TYPES } from '../../../business-logic/bindings/Binding'
import { remapFieldBindings } from './remapFieldBindings'
import type {
  BuilderConnection,
  EditorSDK,
} from '@wix/wix-data-client-common-standalone'
import type { Schema } from '@wix/wix-data-schema-types'

const adjustComponentBindings = async (
  componentRef: ComponentRef,
  controllerRef: ComponentRef,
  newBindings: unknown[],
  role: string,
  appApi: AppApi,
) => {
  if (newBindings.length === 0) {
    await appApi.disconnectBindingSection({
      componentRef,
      controllerRef,
      role,
    })
  } else {
    await appApi.connectBindingSection({
      componentRef,
      controllerRef,
      newBindings: newBindings as any[],
      role,
      requiresPrimaryConnection: true,
      isUserInputFilter: false,
      newColumns: [],
    })
  }
}

const extractFieldBindings = (bindings: BindingConfig[]) =>
  bindings.filter(b => b.type === BINDING_TYPES.Field) as FieldBindingConfig[]

const extractNonFieldBindings = (bindings: BindingConfig[]) =>
  bindings.filter(b => b.type !== BINDING_TYPES.Field)

export const rebindComponentsToAnotherCollection = async ({
  fieldMap,
  controllerRef,
  newCollectionId,
  collectionsApi,
  appApi,
  respectMainProperty = false,
}: {
  fieldMap: Record<string, string>
  controllerRef: ComponentRef
  newCollectionId: string
  collectionsApi: CollectionsApi
  appApi: AppApi
  respectMainProperty?: boolean
}) => {
  const [connections, newSchema] = await Promise.all([
    appApi.fetchControllerBindings({
      controllerRef,
    }),
    collectionsApi.get(newCollectionId),
  ])

  for (const controllerBinding of connections) {
    const { role, bindings, componentRef, componentType } = controllerBinding

    const fieldBindings = extractFieldBindings(bindings)

    if (isEmpty(fieldBindings)) {
      continue
    }

    const bindingDefinition = await appApi.getBindingDefinition({
      componentType,
    })

    const newFieldBindings = remapFieldBindings(
      fieldBindings,
      fieldMap,
      respectMainProperty,
      newSchema,
      bindingDefinition,
      role,
    )

    const nonFieldBindings = extractNonFieldBindings(bindings)
    const mergedBindings = [...nonFieldBindings, ...newFieldBindings]

    await adjustComponentBindings(
      componentRef,
      controllerRef,
      mergedBindings,
      role,
      appApi,
    )
  }
}

export const rebindBuilderComponentsToAnotherCollection = async ({
  fieldMap,
  controllerRef,
  editorSdkProxy,
  newCollectionId,
  collectionsApi,
}: {
  fieldMap: Record<string, string>
  controllerRef: ComponentRef
  editorSdkProxy: EditorSDK
  newCollectionId: string
  collectionsApi: CollectionsApi
}) => {
  const newCollectionSchema = await collectionsApi.get(newCollectionId)
  const connections =
    (await editorSdkProxy.controllers.getControllerConnections({
      controllerRef,
    })) as {
      componentRef: ComponentRef
      connection: BuilderConnection
    }[]

  const updatedConnections = connections.map(({ componentRef, connection }) => {
    const updatedConnection = updateBuilderConnectionFieldKey(
      connection,
      fieldMap,
      newCollectionSchema,
    )
    return editorSdkProxy.controllers.connect({
      controllerRef,
      connectToRef: componentRef,
      role: connection.role,
      connectionConfig: updatedConnection,
    })
  })
  return Promise.all(updatedConnections)
}

function updateBuilderConnectionFieldKey(
  connection: BuilderConnection,
  fieldMap: Record<string, string>,
  schema: Schema,
) {
  const { config } = connection
  const updatedConfig = cloneDeep(config)
  const { byItem } = updatedConfig
  for (const [key, value] of Object.entries(byItem)) {
    const fieldKey = value.config.fieldKey
    const newFieldKey = fieldMap[fieldKey]
    if (newFieldKey) {
      value.config.fieldKey = newFieldKey
      value.target = schema.fields[newFieldKey].displayName
      //TODO: change icon?
    } else {
      delete byItem[key]
    }
  }
  return updatedConfig
}
